import type { ReactNode } from "react";
import { Chip, Stack, Typography } from "@mui/material";

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  compact = false
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  actions?: ReactNode;
  // A smaller title for pages where the header is context, not the point — e.g. a video
  // waiting on a review step, where a three-line title pushes the editor below the fold.
  compact?: boolean;
}) {
  return (
    <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ md: "flex-end" }}>
      {/* minWidth 0 so a long, unbreakable title (a URL, a slug) can't push this column
          wider than the row and shove the actions off the right edge of a phone. */}
      <Stack spacing={1} sx={{ minWidth: 0 }}>
        {eyebrow ? <Chip size="small" color="primary" variant="outlined" label={eyebrow} sx={{ width: "fit-content", maxWidth: "100%" }} /> : null}
        <Typography
          component="h1"
          variant="h3"
          fontWeight={850}
          sx={{
            fontSize: compact ? { xs: "1.35rem", md: "1.6rem" } : { xs: "2rem", sm: "2.35rem", md: "3rem" },
            letterSpacing: compact ? "-0.02em" : undefined,
            overflowWrap: "anywhere"
          }}
        >
          {title}
        </Typography>
        {description ? <Typography color="text.secondary" maxWidth={720}>{description}</Typography> : null}
      </Stack>
      {/* Pages pass anywhere from one button to six (source detail: prev/next/status/
          regenerate/stop/delete). Unwrapped, that row is far wider than a phone screen
          and simply ran off the right edge — it has to wrap. */}
      {actions ? (
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          useFlexGap
          alignItems="center"
          sx={{ alignSelf: { xs: "flex-start", md: "flex-end" }, maxWidth: "100%" }}
        >
          {actions}
        </Stack>
      ) : null}
    </Stack>
  );
}
